import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../firebaseConfig';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import './TimeOffAdmin.css';
import AttendanceLog from './AttendanceLog';
import ScopedPortalPopup from '../components/ScopedPortalPopup';
import {
  isAbsence,
  isPartialDay,
  describeEntry,
  formatYmd as sharedFormatYmd,
} from '../utils/timeOffUtils';

/* ---------------------------------------------------------------------------
   WHAT WAS REMOVED AND WHY

   This file began life as a copy of the Admin Portal, so it carried a large
   amount of code that this screen could never reach:

   - The job details modal and its edit form. `setSelectedJob` was only ever
     called from inside that modal's own handlers, so with no button anywhere
     in the render to open it, `selectedJob` was permanently null.
   - The notices list, the site-name lookup, the deposit-price and change-price
     popups, the pagination for jobs / notices / users, `moment`, and
     `markOccurrenceCompleted`. None were rendered by this page.
   - `handleSearch`, which filtered on `item.siteName` - a field time off
     requests do not have, so it would have thrown had anything called it.
     Nothing did: the search box was wired to state that no list ever read,
     which is why typing in it did nothing.

   What is left is the time off queue and the actions a reviewer takes on it.
--------------------------------------------------------------------------- */

/* ---------------------------------------------------------------------------
   DATE HELPERS

   Dates are stored as "YYYY-MM-DD" strings. new Date("2025-03-10") parses as
   UTC midnight, which renders as the PREVIOUS day west of Greenwich, so the
   date is rebuilt from its parts as a local date.
--------------------------------------------------------------------------- */
const parseYmd = (ymd) => {
  if (!ymd || typeof ymd !== 'string') return null;
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const formatYmd = (ymd) => {
  const date = parseYmd(ymd);
  if (!date) return ymd || '';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatRange = (start, end) => {
  if (!start) return '';
  if (!end || end === start) return formatYmd(start);
  return `${formatYmd(start)} - ${formatYmd(end)}`;
};

const countDays = (start, end) => {
  const s = parseYmd(start);
  const e = parseYmd(end || start);
  if (!s || !e) return 0;
  return Math.round((e - s) / 86400000) + 1;
};

const normalizeName = (value) => String(value || '').trim().toLowerCase();

/* ---------------------------------------------------------------------------
   ACTIVE / INACTIVE PEOPLE

   Different user records have carried this flag under different names over
   time, so all known spellings are checked. An unrecognised record is treated
   as ACTIVE on purpose: hiding a pending request because a field is spelled
   unexpectedly would mean it never gets reviewed.
--------------------------------------------------------------------------- */
const INACTIVE_STATUS_WORDS = [
  'terminated',
  'inactive',
  'former',
  'separated',
  'archived',
  'disabled',
  'resigned',
];

const isActiveUser = (user) => {
  if (!user) return true;
  if (user.active === false) return false;
  if (user.isActive === false) return false;
  if (user.terminated === true) return false;
  const status = String(user.status || user.employmentStatus || '').trim().toLowerCase();
  if (INACTIVE_STATUS_WORDS.includes(status)) return false;
  return true;
};

const statusChipClass = (status) => {
  const s = String(status || '').trim().toLowerCase();
  if (s === 'approved') return 'toa-chip toa-chip-approved';
  if (s === 'denied') return 'toa-chip toa-chip-denied';
  if (s === 'pending') return 'toa-chip toa-chip-pending';
  return 'toa-chip toa-chip-off';
};

const ITEMS_PER_PAGE = 25;

// Partial days show the time instead of a range.
const formatEntryDates = (entry) =>
  isPartialDay(entry)
    ? `${sharedFormatYmd(entry.startDate)} · ${describeEntry(entry)}`
    : formatRange(entry.startDate, entry.endDate);

const TimeOffAdmin = () => {
  const [users, setUsers] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [selectedTimeOffRequest, setSelectedTimeOffRequest] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('');
  // 'requests' = planned time off to approve. 'attendance' = call-outs,
  // left early, came in late.
  const [tab, setTab] = useState('requests');
  const [loading, setLoading] = useState(true);

  const [noteText, setNoteText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [working, setWorking] = useState(false);

  /* Toolbar */
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [hideInactive, setHideInactive] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: 'startDate',
    direction: 'ascending',
  });
  const [page, setPage] = useState(1);

  /* One confirmation sheet serves approve, deny and delete. */
  const [confirmState, setConfirmState] = useState(null);

  const auth = getAuth();
  const navigate = useNavigate();

  const askConfirm = (config) => setConfirmState(config);
  const closeConfirm = () => setConfirmState(null);

  /* ---------------------------------------------------------------- load -- */

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'users'));
        const userList = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUsers(userList);

        const currentUser = auth.currentUser;
        if (currentUser) {
          const userDoc = querySnapshot.docs.find((d) => d.id === currentUser.uid);
          if (userDoc) {
            const data = userDoc.data();
            setUserRole(data.role);
            setCurrentUserName(`${data.firstName || ''} ${data.lastName || ''}`.trim());
          }
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    const fetchTimeOffRequests = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'timeOff'));
        setTimeOffRequests(querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error('Error fetching time off requests:', error);
      }
    };

    const load = async () => {
      await Promise.all([fetchUsers(), fetchTimeOffRequests()]);
      setLoading(false);
    };
    load();
  }, [auth]);

  /* ------------------------------------------------------------- actions -- */

  const handleApproveRequest = async (id) => {
    setWorking(true);
    try {
      await updateDoc(doc(db, 'timeOff', id), { status: 'Approved' });
      setTimeOffRequests((prev) =>
        prev.map((req) => (req.id === id ? { ...req, status: 'Approved' } : req))
      );
      setSelectedTimeOffRequest((prev) => (prev ? { ...prev, status: 'Approved' } : prev));
      closeConfirm();
    } catch (error) {
      console.error('Error approving request:', error);
    } finally {
      setWorking(false);
    }
  };

  const handleDenyRequest = async (id) => {
    setWorking(true);
    try {
      await updateDoc(doc(db, 'timeOff', id), { status: 'Denied' });
      setTimeOffRequests((prev) =>
        prev.map((req) => (req.id === id ? { ...req, status: 'Denied' } : req))
      );
      setSelectedTimeOffRequest((prev) => (prev ? { ...prev, status: 'Denied' } : prev));
      closeConfirm();
    } catch (error) {
      console.error('Error denying request:', error);
    } finally {
      setWorking(false);
    }
  };

  const handleDeleteRequest = async (id) => {
    setWorking(true);
    try {
      await deleteDoc(doc(db, 'timeOff', id));
      setTimeOffRequests((prev) => prev.filter((req) => req.id !== id));
      setSelectedTimeOffRequest(null);
      closeConfirm();
    } catch (error) {
      console.error('Error deleting request:', error);
    } finally {
      setWorking(false);
    }
  };

  // Approving or denying is what the person waiting actually feels, so both go
  // through the same confirmation step as deleting.
  const requestApprove = (request) =>
    askConfirm({
      title: 'Approve this request?',
      message: `${request.name || 'This person'} will be marked approved for ${formatRange(
        request.startDate,
        request.endDate
      )}.`,
      confirmLabel: 'Yes, approve',
      tone: 'green',
      onConfirm: () => handleApproveRequest(request.id),
    });

  const requestDeny = (request) =>
    askConfirm({
      title: 'Deny this request?',
      message: `${request.name || 'This person'} will be marked denied for ${formatRange(
        request.startDate,
        request.endDate
      )}. Add a note first if they should know why.`,
      confirmLabel: 'Yes, deny',
      tone: 'red',
      onConfirm: () => handleDenyRequest(request.id),
    });

  const requestDelete = (request) =>
    askConfirm({
      title: isAbsence(request) ? 'Delete this attendance entry?' : 'Delete this request?',
      message: isAbsence(request)
        ? 'This removes the entry and its notes permanently, and takes it off the calendar.'
        : 'This removes the request and its notes permanently. Deny it instead if you want to keep the record.',
      confirmLabel: 'Yes, delete',
      tone: 'red',
      onConfirm: () => handleDeleteRequest(request.id),
    });

  const handleSaveTimeOffNote = async () => {
    if (!selectedTimeOffRequest) return;
    if (noteText.trim() === '' && selectedFiles.length === 0) return;

    setUploading(true);
    try {
      let uploadedFiles = [];
      if (selectedFiles.length > 0) {
        const storage = getStorage();
        uploadedFiles = await Promise.all(
          selectedFiles.map(async (file) => {
            const storageRef = ref(
              storage,
              `timeOff/${selectedTimeOffRequest.id}/${file.name}`
            );
            await uploadBytes(storageRef, file);
            return await getDownloadURL(storageRef);
          })
        );
      }

      const newNote = {
        text: noteText,
        timestamp: new Date().toLocaleString(),
        files: uploadedFiles,
      };

      const updatedNotes = Array.isArray(selectedTimeOffRequest.notes)
        ? [...selectedTimeOffRequest.notes, newNote]
        : [newNote];

      await updateDoc(doc(db, 'timeOff', selectedTimeOffRequest.id), {
        notes: updatedNotes,
      });

      setSelectedTimeOffRequest((prev) => ({ ...prev, notes: updatedNotes }));
      setTimeOffRequests((prev) =>
        prev.map((req) =>
          req.id === selectedTimeOffRequest.id ? { ...req, notes: updatedNotes } : req
        )
      );

      setNoteText('');
      setSelectedFiles([]);
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setUploading(false);
    }
  };

  const closeRequestModal = () => {
    setSelectedTimeOffRequest(null);
    setNoteText('');
    setSelectedFiles([]);
  };

  /* --------------------------------------------------- derived list data -- */

  // Requests store the person's NAME, not a stable user id, so the active
  // lookup is keyed on the normalized name.
  const activeNameSet = useMemo(() => {
    const set = new Set();
    users.forEach((user) => {
      if (isActiveUser(user)) {
        set.add(normalizeName(`${user.firstName || ''} ${user.lastName || ''}`));
      }
    });
    return set;
  }, [users]);

  const knownNameSet = useMemo(() => {
    const set = new Set();
    users.forEach((user) => {
      set.add(normalizeName(`${user.firstName || ''} ${user.lastName || ''}`));
    });
    return set;
  }, [users]);

  const isRequestForActivePerson = (request) => {
    const name = normalizeName(request.name);
    if (!name || !knownNameSet.has(name)) return true;
    return activeNameSet.has(name);
  };

  const filteredRequests = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    let rows = timeOffRequests.filter((request) => {
      // Attendance entries have their own tab.
      if (isAbsence(request)) return false;
      if (hideInactive && !isRequestForActivePerson(request)) return false;
      if (statusFilter && String(request.status || '') !== statusFilter) return false;
      if (!q) return true;
      return [request.name, request.paidTimeType, request.reason, request.status]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });

    const { key, direction } = sortConfig;
    rows = [...rows].sort((a, b) => {
      let av;
      let bv;
      if (key === 'startDate') {
        // Requests with no date sort last rather than jumping to the top,
        // which is what an empty string does under a plain string compare.
        av = a.startDate || '9999-12-31';
        bv = b.startDate || '9999-12-31';
      } else if (key === 'days') {
        av = countDays(a.startDate, a.endDate);
        bv = countDays(b.startDate, b.endDate);
        return direction === 'ascending' ? av - bv : bv - av;
      } else {
        av = String(a[key] || '');
        bv = String(b[key] || '');
      }
      const cmp = String(av).localeCompare(String(bv));
      return direction === 'ascending' ? cmp : -cmp;
    });

    return rows;
  }, [
    timeOffRequests,
    searchQuery,
    statusFilter,
    hideInactive,
    sortConfig,
    activeNameSet,
    knownNameSet,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / ITEMS_PER_PAGE));

  // Narrowing the filters can leave the current page past the end of the list,
  // which would show an empty table with rows still available on page 1.
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paginatedRequests = filteredRequests.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const stats = useMemo(() => {
    const counts = { pending: 0, approved: 0, denied: 0 };
    timeOffRequests.forEach((r) => {
      if (isAbsence(r)) return;
      const s = String(r.status || 'Pending').trim().toLowerCase();
      if (s === 'approved') counts.approved += 1;
      else if (s === 'denied') counts.denied += 1;
      else counts.pending += 1;
    });
    return counts;
  }, [timeOffRequests]);

  const requestCount = useMemo(
    () => timeOffRequests.filter((r) => !isAbsence(r)).length,
    [timeOffRequests]
  );

  const activePeople = useMemo(
    () =>
      users
        .filter(isActiveUser)
        .map((u) => ({ id: u.id, name: `${u.firstName || ''} ${u.lastName || ''}`.trim() }))
        .filter((u) => u.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [users]
  );

  const handleAttendanceSaved = (entry, mode) => {
    setTimeOffRequests((prev) =>
      mode === 'created'
        ? [...prev, entry]
        : prev.map((r) => (r.id === entry.id ? { ...r, ...entry } : r))
    );
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
    }));
    setPage(1);
  };

  const SortableHeader = ({ columnKey, label }) => {
    const active = sortConfig.key === columnKey;
    return (
      <th>
        <button
          type="button"
          className={`toa-sort-button${active ? ' toa-sort-active' : ''}`}
          onClick={() => handleSort(columnKey)}
          aria-sort={active ? sortConfig.direction : 'none'}
        >
          {label}
          <span className="toa-sort-caret">
            {active ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : '↕'}
          </span>
        </button>
      </th>
    );
  };

  /* -------------------------------------------------------------- render -- */

  return (
    <div className="time-off-admin-page">
      <header className="toa-header">
        <div>
          <h1 className="toa-title">Time Off Approvals</h1>
          <p className="toa-subtitle">
            Review, approve or deny time off, and log when someone calls out,
            leaves early or comes in late. Both show on the calendar.
          </p>
        </div>
      </header>

      <div className="toa-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'requests'}
          className={`toa-tab${tab === 'requests' ? ' toa-tab-active' : ''}`}
          onClick={() => setTab('requests')}
        >
          Requests
          {stats.pending > 0 && <span className="toa-tab-badge">{stats.pending}</span>}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'attendance'}
          className={`toa-tab${tab === 'attendance' ? ' toa-tab-active' : ''}`}
          onClick={() => setTab('attendance')}
        >
          Call-outs &amp; left early
        </button>
      </div>

      {tab === 'attendance' ? (
        loading ? (
          <p className="toa-empty">Loading...</p>
        ) : (
          <AttendanceLog
            entries={timeOffRequests}
            users={users}
            activePeople={activePeople}
            isForActivePerson={isRequestForActivePerson}
            hideInactive={hideInactive}
            setHideInactive={setHideInactive}
            currentUserName={currentUserName}
            onSaved={handleAttendanceSaved}
            onOpenDetails={setSelectedTimeOffRequest}
          />
        )
      ) : (
      <>

      <div className="toa-stats">
        <div className="toa-stat toa-stat-pending">
          <span className="toa-stat-value">{stats.pending}</span>
          <span className="toa-stat-label">Awaiting review</span>
        </div>
        <div className="toa-stat toa-stat-approved">
          <span className="toa-stat-value">{stats.approved}</span>
          <span className="toa-stat-label">Approved</span>
        </div>
        <div className="toa-stat toa-stat-denied">
          <span className="toa-stat-value">{stats.denied}</span>
          <span className="toa-stat-label">Denied</span>
        </div>
      </div>

      <div className="toa-toolbar">
        <div className="toa-field toa-field-grow">
          <span className="toa-field-label" id="toa-search-label">
            Search
          </span>
          <input
            aria-labelledby="toa-search-label"
            type="search"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Name, type or reason"
          />
        </div>

        <div className="toa-field">
          <span className="toa-field-label" id="toa-status-label">
            Status
          </span>
          <select
            aria-labelledby="toa-status-label"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Denied">Denied</option>
          </select>
        </div>

        <label className="toa-toggle">
          <input
            type="checkbox"
            checked={hideInactive}
            onChange={(e) => {
              setHideInactive(e.target.checked);
              setPage(1);
            }}
          />
          Hide people no longer active
        </label>

        <div className="toa-count">
          {filteredRequests.length} of {requestCount}
        </div>
      </div>

      <div className="toa-table-wrap">
        <table className="toa-table">
          <thead>
            <tr>
              <SortableHeader columnKey="name" label="Person" />
              <SortableHeader columnKey="startDate" label="Dates" />
              <SortableHeader columnKey="days" label="Days" />
              <SortableHeader columnKey="paidTimeType" label="Type" />
              <SortableHeader columnKey="dateRequested" label="Submitted" />
              <SortableHeader columnKey="status" label="Status" />
              <th className="toa-col-actions">
                <span className="toa-th-plain">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="toa-empty" colSpan={7}>
                  Loading requests...
                </td>
              </tr>
            ) : paginatedRequests.length === 0 ? (
              <tr>
                <td className="toa-empty" colSpan={7}>
                  {requestCount === 0
                    ? 'No time off has been requested yet.'
                    : 'No requests match the current search and filters.'}
                </td>
              </tr>
            ) : (
              paginatedRequests.map((request) => {
                const days = isPartialDay(request)
                  ? 'Partial'
                  : countDays(request.startDate, request.endDate);
                const inactive = !isRequestForActivePerson(request);
                const pending =
                  String(request.status || 'Pending').trim().toLowerCase() === 'pending';
                const rowClass = [
                  pending ? 'toa-row-pending' : '',
                  inactive ? 'toa-row-inactive' : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <tr key={request.id} className={rowClass || undefined}>
                    <td>
                      <span className="toa-strong">{request.name || 'Unknown'}</span>
                      {inactive && <span className="toa-sub">No longer active</span>}
                    </td>
                    <td>{formatEntryDates(request)}</td>
                    <td>{days || <span className="toa-muted">—</span>}</td>
                    <td>{request.paidTimeType || <span className="toa-muted">—</span>}</td>
                    <td>
                      {request.dateRequested ? (
                        new Date(request.dateRequested).toLocaleDateString()
                      ) : (
                        <span className="toa-muted">—</span>
                      )}
                    </td>
                    <td>
                      <span className={statusChipClass(request.status)}>
                        {request.status || 'Pending'}
                      </span>
                    </td>
                    <td className="toa-col-actions">
                      <div className="button-group">
                        <button
                          className="blue-button"
                          onClick={() => setSelectedTimeOffRequest(request)}
                        >
                          Review
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="toa-pagination">
          <button
            className="grey-button"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            className="grey-button"
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
      </>
      )}

      {userRole === 'Admin' && (
        <div className="toa-footer">
          <button className="grey-button" onClick={() => navigate('/AdminPortal')}>
            Go to Admin Portal
          </button>
        </div>
      )}

      {/* -------------------------------------------- Review request ------ */}
      {selectedTimeOffRequest && (
        <ScopedPortalPopup onClose={closeRequestModal} scopeClass="time-off-admin-page toa-popup">
            <h3>{isAbsence(selectedTimeOffRequest) ? 'Attendance entry' : 'Review request'}</h3>
            <p className="toa-modal-lead">
              {selectedTimeOffRequest.name || 'Unknown'} ·{' '}
              {formatEntryDates(selectedTimeOffRequest)}
            </p>

            <div className="toa-modal-scroll toa-detail">
              {isAbsence(selectedTimeOffRequest) ? (
                <>
                  <p>
                    <strong>What happened:</strong> {describeEntry(selectedTimeOffRequest)}
                  </p>
                  {!isPartialDay(selectedTimeOffRequest) && (
                    <p>
                      <strong>Days out:</strong>{' '}
                      {countDays(
                        selectedTimeOffRequest.startDate,
                        selectedTimeOffRequest.endDate
                      )}
                    </p>
                  )}
                  <p>
                    <strong>Reason:</strong>{' '}
                    {selectedTimeOffRequest.reasonCategory || '—'}
                    {selectedTimeOffRequest.reason ? ` - ${selectedTimeOffRequest.reason}` : ''}
                  </p>
                  <p>
                    <strong>Notice given:</strong> {selectedTimeOffRequest.noticeGiven || '—'}
                  </p>
                  {selectedTimeOffRequest.notifiedAt && !selectedTimeOffRequest.noCallNoShow && (
                    <p>
                      <strong>Told us:</strong>{' '}
                      {new Date(selectedTimeOffRequest.notifiedAt).toLocaleString()}
                    </p>
                  )}
                  <p>
                    <strong>Pay as:</strong> {selectedTimeOffRequest.paidTimeType || '—'}
                  </p>
                  <p>
                    <strong>Logged by:</strong> {selectedTimeOffRequest.loggedBy || '—'}
                    {selectedTimeOffRequest.loggedAt
                      ? ` on ${new Date(selectedTimeOffRequest.loggedAt).toLocaleString()}`
                      : ''}
                  </p>
                </>
              ) : (
                <>
                  <p>
                    <strong>Type:</strong> {describeEntry(selectedTimeOffRequest)}
                  </p>
                  <p>
                    <strong>Days requested:</strong>{' '}
                    {isPartialDay(selectedTimeOffRequest)
                      ? 'Partial day'
                      : countDays(
                          selectedTimeOffRequest.startDate,
                          selectedTimeOffRequest.endDate
                        )}
                  </p>
                  <p>
                    <strong>Paid time type:</strong>{' '}
                    {selectedTimeOffRequest.paidTimeType || '—'}
                  </p>
                  <p>
                    <strong>Reason:</strong> {selectedTimeOffRequest.reason || '—'}
                  </p>
                  <p>
                    <strong>Status:</strong>{' '}
                    <span className={statusChipClass(selectedTimeOffRequest.status)}>
                      {selectedTimeOffRequest.status || 'Pending'}
                    </span>
                  </p>
                  <p>
                    <strong>Submitted:</strong>{' '}
                    {selectedTimeOffRequest.dateRequested
                      ? new Date(selectedTimeOffRequest.dateRequested).toLocaleString()
                      : '—'}
                  </p>
                </>
              )}

              <label htmlFor="toa-note">Notes</label>
              <div className="toa-notes">
                {Array.isArray(selectedTimeOffRequest.notes) &&
                selectedTimeOffRequest.notes.length > 0 ? (
                  <ul>
                    {selectedTimeOffRequest.notes.map((note, index) => (
                      <li key={index}>
                        <span className="toa-note-time">{note.timestamp}</span>
                        {note.text}
                        {note.files &&
                          note.files.map((fileURL, fileIndex) => (
                            <a
                              key={fileIndex}
                              href={fileURL}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {/\.(jpeg|jpg|gif|png)$/i.test(fileURL) ? (
                                <img
                                  src={fileURL}
                                  alt={`Attachment ${fileIndex + 1}`}
                                  className="thumbnail"
                                />
                              ) : (
                                ` Attachment ${fileIndex + 1}`
                              )}
                            </a>
                          ))}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="toa-sub">No notes yet.</span>
                )}
              </div>

              <div className="toa-form-field">
                <textarea
                  id="toa-note"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note the requester will see"
                />
              </div>
              <div className="toa-form-field">
                <label htmlFor="toa-note-files">Attach files</label>
                <input
                  id="toa-note-files"
                  type="file"
                  multiple
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                />
              </div>
            </div>

            <div className="button-group">
              <button
                className="grey-button"
                onClick={handleSaveTimeOffNote}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Add note'}
              </button>
              {!isAbsence(selectedTimeOffRequest) && (
                <>
                  <button
                    className="green-button"
                    onClick={() => requestApprove(selectedTimeOffRequest)}
                  >
                    Approve
                  </button>
                  <button
                    className="red-button"
                    onClick={() => requestDeny(selectedTimeOffRequest)}
                  >
                    Deny
                  </button>
                </>
              )}
              <button
                className="red-button"
                onClick={() => requestDelete(selectedTimeOffRequest)}
              >
                Delete
              </button>
              <button className="grey-button" onClick={closeRequestModal}>
                Close
              </button>
            </div>
        </ScopedPortalPopup>
      )}

      {/* --------------------------------------------- Confirm sheet ------ */}
      {confirmState && (
        <ScopedPortalPopup
          onClose={closeConfirm}
          scopeClass="time-off-admin-page toa-popup"
          level={1}
          width={460}
          role="alertdialog"
        >
            <h3>{confirmState.title}</h3>
            <p className="toa-modal-lead">{confirmState.message}</p>
            <div className="button-group">
              <button
                className={confirmState.tone === 'red' ? 'red-button' : 'green-button'}
                onClick={confirmState.onConfirm}
                disabled={working}
              >
                {working ? 'Working...' : confirmState.confirmLabel}
              </button>
              <button className="grey-button" onClick={closeConfirm} disabled={working}>
                Cancel
              </button>
            </div>
        </ScopedPortalPopup>
      )}
    </div>
  );
};

export default TimeOffAdmin;
